<template>
  <Tooltip>
    <TooltipTrigger as-child>
      <button
        type="button"
        class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-full border-[1.5px] transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :class="lead.planned ? FOLLOW_UP_PLANNED_CLASSES : FOLLOW_UP_EVENT_STYLES[lead.kind].classes"
        :style="{left: `${cluster.position * 100}%`}"
        :aria-label="heading"
      >
        <component :is="FOLLOW_UP_EVENT_STYLES[lead.kind].icon" class="size-3.5" />
        <span
          v-if="cluster.events.length > 1"
          class="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-tiny font-semibold leading-4 text-center"
        >
          {{ cluster.events.length }}
        </span>
      </button>
    </TooltipTrigger>
    <TooltipContent class="bg-darkPurple text-white max-w-sm p-3 shadow-deep" avoid-collisions>
      <p class="font-semibold mb-2">
        {{ heading }}
      </p>
      <ul class="flex flex-col gap-2">
        <li v-for="event in cluster.events" :key="event.id" class="flex flex-col gap-0.5">
          <p class="flex items-center gap-1.5 font-medium">
            <component :is="FOLLOW_UP_EVENT_STYLES[event.kind].icon" class="size-3.5 shrink-0" />
            <span>{{ event.title }}</span>
            <span v-if="cluster.events.length > 1" class="opacity-75 shrink-0">· {{ formatDate(event.date) }}{{ event.planned ? ' (planifié)' : '' }}</span>
          </p>
          <ConversationMessagePreview
            v-if="event.message && cluster.events.length === 1"
            :message="event.message"
            class="mt-1 bg-white text-gray-dark rounded-lg"
          />
          <p v-else-if="event.detail" class="opacity-90 line-clamp-3">
            {{ event.detail }}
          </p>
        </li>
      </ul>
    </TooltipContent>
  </Tooltip>
</template>

<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/core/components/ui/tooltip";
import ConversationMessagePreview from "@/conversation/components/ConversationMessagePreview.vue";
import {FollowUpCluster, FOLLOW_UP_EVENT_LABELS, FOLLOW_UP_EVENT_PRIORITY} from "@/program/services/ProgramFollowUpService";
import {FOLLOW_UP_EVENT_STYLES, FOLLOW_UP_PLANNED_CLASSES} from "@/program/components/program-follow-up/followUpEventStyles";

const props = defineProps<{
    cluster: FollowUpCluster
}>();

const first = computed(() => props.cluster.events[0]);

/**
 * The event the marker is drawn as: the one of highest priority, the earliest
 * of them on a tie.
 */
const lead = computed(() => props.cluster.events.reduce((lead, event) => {
    return FOLLOW_UP_EVENT_PRIORITY[event.kind] < FOLLOW_UP_EVENT_PRIORITY[lead.kind] ? event : lead;
}));

/**
 * The tooltip heading: the event kind and date, or the range of the grouped events.
 */
const heading = computed(() => {
    const events = props.cluster.events;

    if (events.length === 1) {
        return `${first.value.planned ? 'À venir' : FOLLOW_UP_EVENT_LABELS[first.value.kind]} · ${formatDate(first.value.date, true)}`;
    }

    const last = events[events.length - 1];
    const range = dayjs(first.value.date).isSame(last.date, 'day')
        ? `le ${formatDate(first.value.date, true)}`
        : `du ${formatDate(first.value.date)} au ${formatDate(last.date, true)}`;

    return `${events.length} évènements ${range}`;
});

/**
 * Format a date in French, e.g. "24 mai" or "24 mai 2026".
 */
const formatDate = (date: Date, withYear = false): string => {
    return dayjs(date).locale('fr').format(withYear ? 'D MMMM YYYY' : 'D MMM');
};
</script>
